import React, { useState, useEffect, useRef } from 'react';
import { CloudUpload, Close, Image as ImageIcon } from '@mui/icons-material';
import { postRequest } from '../../../../config';

let _imgUploaderCounter = 0;

const ImageUploader = ({ imageURL, onImageChange, label = "Module Image" }) => {
    const idRef = useRef(`img-uploader-${++_imgUploaderCounter}`);
    const inputId = idRef.current;
    const changeId = `${inputId}-change`;

    const [preview, setPreview] = useState(imageURL || '');
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Sync preview with imageURL prop changes
    useEffect(() => {
        setPreview(imageURL || '');
    }, [imageURL]);

    const handleFileSelect = async (file) => {
        if (!file) return;

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert('Image size must be less than 5MB');
            return;
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please upload an image file');
            return;
        }

        try {
            setIsUploading(true);
            setUploadProgress(0);

            // Create preview for immediate feedback
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result);
            };
            reader.readAsDataURL(file);

            // Upload file to server
            const res = await postRequest('/question/newUploadFile', { attachment: [file] }, true);

            if (!res || res.code !== 200) {
                throw new Error('Upload failed');
            }

            const uploadedFile = res.body?.result;

            if (!uploadedFile || !uploadedFile.location) {
                throw new Error('Invalid upload response');
            }

            // Get the file URL from the response (S3 location)
            const fileUrl = uploadedFile.location;

            // Update preview and notify parent
            setPreview(fileUrl);
            onImageChange(fileUrl);
            setUploadProgress(100);

        } catch (error) {
            console.error('Upload error:', error);
            alert('Failed to upload image. Please try again.');
            setPreview('');
            onImageChange('');
        } finally {
            setIsUploading(false);
            setTimeout(() => setUploadProgress(0), 1000);
        }
    };

    const handleChange = (e) => {
        const file = e.target.files[0];
        handleFileSelect(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        handleFileSelect(file);
    };

    const handleClear = () => {
        setPreview('');
        onImageChange('');
    };

    return (
        <div className="df fdc">
            <label className="fs0-9rem fw600 c15 mb0-75rem">{label}</label>

            {!preview ? (
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className="br10px bss bw2px cp"
                    style={{
                        border: isDragging ? '2px dashed #4CAF50' : '2px dashed #ddd',
                        background: isDragging ? '#f1f8f4' : '#fafafa',
                        padding: '2rem',
                        transition: 'all 0.3s ease',
                        position: 'relative'
                    }}
                >
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleChange}
                        style={{ display: 'none' }}
                        id={inputId}
                        disabled={isUploading}
                    />
                    <label
                        htmlFor={inputId}
                        className="df fdc aic jcc cp"
                        style={{ cursor: isUploading ? 'not-allowed' : 'pointer', opacity: isUploading ? 0.6 : 1 }}
                    >
                        <div
                            className="df aic jcc"
                            style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '50%',
                                background: isUploading
                                    ? 'linear-gradient(135deg, #999 0%, #666 100%)'
                                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                marginBottom: '1rem',
                                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                                position: 'relative'
                            }}
                        >
                            {isUploading ? (
                                <div className="df fdc aic jcc">
                                    <div
                                        style={{
                                            width: '24px',
                                            height: '24px',
                                            border: '3px solid rgba(255,255,255,0.3)',
                                            borderTop: '3px solid white',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite'
                                        }}
                                    />
                                </div>
                            ) : (
                                <CloudUpload style={{ fontSize: '32px', color: 'white' }} />
                            )}
                        </div>
                        <p className="fs1rem fw600 mb0-5rem" style={{ color: '#333', margin: '0 0 0.5rem 0' }}>
                            {isUploading ? 'Uploading...' : 'Upload Module Image'}
                        </p>
                        <p className="fs0-875rem mb0-75rem" style={{ color: '#666', margin: '0 0 0.75rem 0' }}>
                            {isUploading ? 'Please wait...' : 'Drag and drop or click to browse'}
                        </p>
                        <p className="fs0-8rem" style={{ color: '#999', margin: 0 }}>
                            PNG, JPG up to 5MB
                        </p>
                    </label>
                    <style>
                        {`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}
                    </style>
                </div>
            ) : (
                <div
                    className="br10px p1rem"
                    style={{
                        border: '1px solid #e0e0e0',
                        background: '#fafafa',
                        position: 'relative'
                    }}
                >
                    <div className="df fdc aic">
                        <div
                            className="w100"
                            style={{
                                height: '200px',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                background: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <img
                                src={preview}
                                alt="Module preview"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = '<div style="color: #999; font-size: 3rem;">🖼️</div>';
                                }}
                            />
                        </div>
                        <div className="df aic mt1rem" style={{ gap: '0.75rem' }}>
                            <label
                                htmlFor={changeId}
                                className="df aic jcc cp"
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    background: isUploading ? '#999' : '#4CAF50',
                                    color: 'white',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    border: 'none',
                                    cursor: isUploading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 2px 6px rgba(76, 175, 80, 0.3)',
                                    opacity: isUploading ? 0.6 : 1
                                }}
                                onMouseOver={(e) => !isUploading && (e.currentTarget.style.background = '#45a049')}
                                onMouseOut={(e) => !isUploading && (e.currentTarget.style.background = '#4CAF50')}
                            >
                                <ImageIcon style={{ fontSize: '16px', marginRight: '6px' }} />
                                {isUploading ? 'Uploading...' : 'Change Image'}
                            </label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleChange}
                                style={{ display: 'none' }}
                                id={changeId}
                                disabled={isUploading}
                            />
                            <button
                                type="button"
                                onClick={handleClear}
                                disabled={isUploading}
                                className="df aic jcc cp"
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    background: 'transparent',
                                    color: '#dc3545',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    border: '1px solid #dc3545',
                                    cursor: isUploading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease',
                                    opacity: isUploading ? 0.6 : 1
                                }}
                                onMouseOver={(e) => {
                                    if (!isUploading) {
                                        e.currentTarget.style.background = '#dc3545';
                                        e.currentTarget.style.color = 'white';
                                    }
                                }}
                                onMouseOut={(e) => {
                                    if (!isUploading) {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.color = '#dc3545';
                                    }
                                }}
                            >
                                <Close style={{ fontSize: '16px', marginRight: '6px' }} />
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <small className="fs11px c15 op60 mt0-5rem" style={{ color: '#999' }}>
                Upload an image file for the module icon/logo
            </small>
        </div>
    );
};

export default ImageUploader;

